import {
  CatalogListItem,
  AdgroupFormData,
  AppRetargetingAudiencePackageType,
  AppRetargetingType,
  CampaignFormData,
  OptimizeGoal,
  RulesType,
  CreateMode,
  AudiencePackageItemStatus,
  AudienceAgeCoverStatusType,
  AudiencePackageItem,
} from '@tt4b/ttam-enums';

import {
  ValidationError,
  useGetAppEventListApi,
  useRequest,
  useStdAdgroupIs,
  useStdObjectiveType,
  useAdgroupMode,
  useAdgroupIos14,
  NetStateEnum,
  useStore,
  StateEnum,
  useSplitTestEnabled,
  useAllowlist,
  useAdgroupAgeStatus,
} from '@tt4b/ttam-hooks';

import { computed, onBeforeMount, Ref, ComputedRef, ref } from '@vue/composition-api';
import { isNumber } from 'lodash-es';
import { i18n } from './../../../i18n';
import { createOptionsGroupBySource } from './tools';
import { AudienceGroupKey, OptionsType } from './types';
import { AUDIENCE_TYPE } from './consts';
import VueI18n from 'vue-i18n';

export function checkIsPremiumAgeAudience(type: AudienceAgeCoverStatusType) {
  return [AudienceAgeCoverStatusType.SixteenAndAbove, AudienceAgeCoverStatusType.TwentyOneAndAbove].includes(type);
}

export function selectedPremiumAgeInfo(selectedIds: number[] = [], options: AudiencePackageItem[]) {
  let premiumAgeInfo: undefined | AudiencePackageItem;
  selectedIds.find(selectedItem => {
    const option = options.find(item => item.id === selectedItem);
    const isPremiumAge = checkIsPremiumAgeAudience(option?.audienceAgeCoverStatus as AudienceAgeCoverStatusType);
    if (isPremiumAge) {
      premiumAgeInfo = option;
    }
    return isPremiumAge;
  });

  return premiumAgeInfo;
}

export function checkIsAudienceItemDisabled({
  item,
  selectedIds,
  options,
  isSelectAllAge,
}: {
  item: AudiencePackageItem;
  selectedIds: { current: number[]; other: number[] };
  options: AudiencePackageItem[];
  isSelectAllAge: boolean;
}): {
  disabled: boolean;
  disabledTip?: VueI18n.TranslateResult;
} {
  const { current = [], other = [] } = selectedIds;
  const currentTypeSelectAudience = Boolean(current.length);
  const existingSelectedPremiumAge = selectedPremiumAgeInfo(current, options) || selectedPremiumAgeInfo(other, options);
  if (checkIsPremiumAgeAudience(item.audienceAgeCoverStatus)) {
    // 1. Premium age 不能和其他 Audience 同时选择，如果已经选择则禁用
    if (currentTypeSelectAudience && !existingSelectedPremiumAge) {
      return {
        disabled: true,
        disabledTip: i18n.t('module_targeting_premium_select_hint', { premium_age_audience_name: item.name }),
      };
    }

    // 2. Premium age 不能和其他 Premium age 同时使用，如果已经存在且非当前则禁用
    if (existingSelectedPremiumAge) {
      const v = existingSelectedPremiumAge.id !== item.id;
      return {
        disabled: v,
        disabledTip: v
          ? i18n.t('module_targeting_audience_disabled_select_hint', {
              type: existingSelectedPremiumAge.name,
            })
          : '',
      };
    }

    // 3. 如果 age 不是全选则禁用
    return {
      disabled: !isSelectAllAge,
      disabledTip: !isSelectAllAge
        ? i18n.t('module_targeting_premium_select_hint', { premium_age_audience_name: item.name })
        : '',
    };
  } else {
    const v = Boolean(selectedPremiumAgeInfo(current, options));
    return {
      disabled: v,
      disabledTip: v
        ? i18n.t('module_targeting_audience_disabled_select_hint', { type: existingSelectedPremiumAge.name })
        : '',
    };
  }
}

export function useRetargetingIs(opt: { adgroupData: Ref<AdgroupFormData>; campaignData: Ref<CampaignFormData> }) {
  const { adgroupData, campaignData } = opt;
  const { isConversion, isProductSales } = useStdObjectiveType({ campaignFormData: campaignData });
  const { isAppDownload, isWebsite, isStandardAdsWithCatalog } = useStdAdgroupIs({
    adgroupFormData: adgroupData,
    campaignFormData: campaignData,
  });
  const isConversionRetargetingMode = computed(() => isConversion.value && isAppDownload.value);
  const isAudienceBasicMode = computed(() => !isConversionRetargetingMode.value);
  // 披露Prospective/Retargeting的需要满足：
  // productSale objective & productSource选择了catalog web & 用户在msite retargeting白名单中
  // productSale objective & productSource选择了catalog app（但如果是ios14DC需要disabled掉Retargeting选项）
  const isProductSaleRetargeting = computed(
    () => isProductSales.value && isStandardAdsWithCatalog.value && (isWebsite.value || isAppDownload.value),
  );
  return {
    isConversionRetargetingMode,
    isAudienceBasicMode,
    isProductSaleRetargeting,
    isProductSales,
  };
}

export function useExpansionPanelLabel(opt: {
  adgroupData: Ref<AdgroupFormData>;
  campaignData: Ref<CampaignFormData>;
}) {
  const { adgroupData, campaignData } = opt;
  const { isConversionRetargetingMode } = useRetargetingIs({ adgroupData, campaignData });
  const label = computed(() =>
    isConversionRetargetingMode.value
      ? i18n.t('module_adgroup_targeting_audience_retargeting_title')
      : i18n.t('adgroup_audience'),
  );
  const labelTip = computed(() => (isConversionRetargetingMode.value ? '' : i18n.t('adgroup_audience_tips')));
  return {
    expansionPanelLabel: label,
    expansionPanelLabelTip: labelTip,
  };
}

export const useCommonCreateNewHref = (opt: { aadvid: string }) => `/i18n_dmp/adver/main?aadvid=${opt?.aadvid}`;

export function useExcludeSelectParams(opt: {
  adgroupData: Ref<AdgroupFormData>;
  campaignData: Ref<CampaignFormData>;
  isRetargeting?: Ref<boolean>;
}) {
  const { adgroupData, campaignData, isRetargeting } = opt;
  // const { isSelectedIndustryType } = useStdAdgroupIs({ adgroupFormData: adgroupData, campaignFormData: campaignData });
  const excludeSelectVisible = ref<boolean>(
    !isRetargeting?.value || Boolean(adgroupData.value?.retargetingTagsExclude?.length),
  );
  const excludeSelectAvailable = computed(() => true);
  return {
    excludeSelectVisible,
    excludeSelectAvailable,
  };
}

export function useDmpInfoDataSource(opt: {
  adgroupData: Ref<AdgroupFormData>;
  campaignData: Ref<CampaignFormData>;
  isRetargeting?: Ref<boolean>;
  isProductSales?: Ref<boolean>;
  from?: Ref<string>;
}) {
  const { adgroupData, campaignData, isRetargeting, isProductSales, from = ref('') } = opt;
  const { isSelectedIndustryType } = useStdAdgroupIs({ adgroupFormData: adgroupData, campaignFormData: campaignData });
  const { isSelectAllAge } = useAdgroupAgeStatus({ adgroupFormData: adgroupData, campaignFormData: campaignData });
  const { getStore } = useStore();
  const { data: dmpInfo, isLoading, run: getDMPInfo } = getStore(NetStateEnum.dmpList);
  const requestDMPInfo = () => {
    if (isLoading.value) {
      return;
    }
    getDMPInfo();
  };
  onBeforeMount(() => {
    requestDMPInfo();
  });
  const filteredOptions = computed(() => {
    let temp = (dmpInfo.value ?? []) as AudiencePackageItem[];
    if (isRetargeting.value) {
      temp = temp.filter(item =>
        [AppRetargetingAudiencePackageType.APP_ACTIVITY, AppRetargetingAudiencePackageType.CUSTOMER_FILE].includes(
          item.audienceType,
        ),
      );
    }
    if (isSelectedIndustryType.value) {
      temp = temp.filter(item => [Number(AudienceGroupKey.custom)].includes(item.source));
    }
    return temp;
  });
  const allIncludeTreeData = computed(() => {
    const allOptions = filteredOptions.value?.map(item => {
      const { disabled, disabledTip } = checkIsAudienceItemDisabled({
        item,
        selectedIds: {
          current: adgroupData.value.retargetingTags,
          other: adgroupData.value.retargetingTagsExclude,
        },
        options: filteredOptions.value,
        isSelectAllAge: isSelectAllAge.value,
      });
      return {
        ...item,
        disabled: adgroupData.value?.retargetingTagsExclude?.includes(item.id) || disabled,
        disabledTip,
      };
    });
    if (!allOptions) {
      return [];
    }
    return isRetargeting.value || isSelectedIndustryType.value
      ? allOptions
      : [...createOptionsGroupBySource(allOptions)];
  });
  const allExcludeTreeData = computed(() => {
    const allOptions = filteredOptions.value?.map(item => {
      const { disabled, disabledTip } = checkIsAudienceItemDisabled({
        item,
        selectedIds: {
          current: adgroupData.value.retargetingTagsExclude,
          other: adgroupData.value.retargetingTags,
        },
        options: filteredOptions.value,
        isSelectAllAge: isSelectAllAge.value,
      });
      return {
        ...item,
        disabled: adgroupData.value?.retargetingTags?.includes(item.id) || disabled,
        disabledTip,
      };
    });
    if (!allOptions) {
      return [];
    }
    if (isSelectedIndustryType.value) {
      return allOptions.filter(
        item =>
          ![AppRetargetingAudiencePackageType.CHALLENGE, AppRetargetingAudiencePackageType.PREMIUM_AUDIENCE].includes(
            item.audienceType,
          ),
      );
    }
    return isRetargeting.value || isSelectedIndustryType.value
      ? allOptions
      : [...createOptionsGroupBySource(allOptions)];
  });

  const includeSelectTreeProps = computed(() => {
    const base = {
      treeProps: { label: 'name' },
      dataTea: 'create_adgroup_audience_include',
      dataTeaFrom: from.value,
    };
    /** app retargeting 人群包选项不同于 dmp */
    if (isRetargeting.value || isSelectedIndustryType.value) {
      return {
        ...base,
        tree: allIncludeTreeData.value,
        // title: i18n.t('modele_adgroup_targeting_audience_retargeting_select_panel_title'),
      };
    }
    if (isProductSales.value) {
      return {
        ...base,
        isTabPanel: true,
        panelData: allIncludeTreeData.value,
      };
    }
    return {
      ...base,
      isTabPanel: true,
      panelData: allIncludeTreeData.value,
      placeholder: i18n.t('adgroup_optional'),
    };
  });

  const excludeSelectTreeProps = computed(() => {
    const base = {
      treeProps: { label: 'name' },
      dataTea: 'create_adgroup_audience_exclude',
      dataTeaFrom: from.value,
    };
    /** app retargeting 人群包选项不同于 dmp */
    if (isRetargeting.value || isSelectedIndustryType.value) {
      return {
        ...base,
        tree: allExcludeTreeData.value,
      };
    }
    if (isProductSales.value) {
      return {
        ...base,
        isTabPanel: true,
        panelData: allExcludeTreeData.value,
      };
    }
    return {
      ...base,
      isTabPanel: true,
      panelData: allExcludeTreeData.value,
      placeholder: i18n.t('adgroup_optional'),
    };
  });
  return {
    isSelectedIndustryType,
    dmpInfo,
    isLoading,
    requestDMPInfo,
    includeSelectTreeProps,
    excludeSelectTreeProps,
  };
}

export function usePropsAudienceDisabled(opt: { adgroupData: Ref<AdgroupFormData> }) {
  const { adgroupData } = opt;
  const isOptimizationGoal = (optimizationGoal: OptimizeGoal): boolean =>
    adgroupData.value.optimizeGoal === optimizationGoal;
  // const { isOptGoalLPV } = useOptimizationGoal();
  const isOptGoalLPV = computed(() => isOptimizationGoal(OptimizeGoal.LANDING_PAGE_VIEW));
  const audienceDisabledTips = computed(() => {
    if (isOptGoalLPV.value) {
      return i18n.t('adgroup_optimize_landing_page_view_disable_dmp');
    }
    return '';
  });
  return {
    isOptGoalLPV,
    audienceDisabledTips,
  };
}

export function useRules() {
  return {
    retargetingAudienceValidator: computed(() => [
      {
        validator: (rule, value: number[], callback) => {
          if (!value?.length) {
            return callback(
              new ValidationError({
                message: i18n.t('module_adgroup_targeting_audience_retargeting_include_validate'),
                messageI18nKey: 'module_adgroup_targeting_audience_retargeting_include_validate',
                moduleName: i18n.t('adgroup_audience_target'),
                subModuleName: i18n.t('adgroup_audience'),
              }),
            );
          }
          return callback();
        },
        trigger: 'change',
      },
    ]),
    customRetargetingValidator: computed(() => [
      {
        validator: (rule, value: AdgroupFormData['retargetingTags'], callback, data: AdgroupFormData) => {
          const { appRetargetingType } = data;
          if (appRetargetingType !== AppRetargetingType.CUSTOM_AUDIENCE) {
            return callback();
          }
          if (!value?.length) {
            return callback(
              new ValidationError({
                messageI18nKey: 'module_adgroupd_audience_group_empty',
                message: i18n.t('module_adgroupd_audience_group_empty'),
                moduleName: i18n.t('adgroup_audience_target'),
                subModuleName: i18n.t('module_dpa_ad_custom_include_title'),
              }),
            );
          }
          return callback();
        },
        trigger: 'change',
      },
    ]),
  };
}

export function useAppEventDataSource(opt: { adgroupData: Ref<AdgroupFormData> }) {
  const { adgroupData } = opt;
  const adRefAppId = computed(() => adgroupData.value.adRefAppId);

  const { data: appEventList, run: getAppEventList } = useRequest(
    useGetAppEventListApi,
    { adRefAppId: adRefAppId.value },
    {
      manual: true,
    },
  );

  const requestAppEventList = () => {
    if (!adRefAppId.value) {
      return;
    }
    getAppEventList();
  };

  onBeforeMount(() => {
    requestAppEventList();
  });

  return {
    appEventList,
    requestAppEventList,
  };
}

export function useAudienceRules(options: OptionsType) {
  if (!options.initRules) {
    throw new Error('initRules is required');
  }
  const defaults = {
    minRulesCount: 0,
    maxRulesCount: 99,
  };
  const settings = Object.assign({}, defaults, options);
  const rules = ref(settings.initRules.value);

  /** Helper */
  const createNewRulesOption = (): RulesType => ({
    code: '',
    days: '',
  });

  /** User event */
  const add = () => {
    if (rules.value.length >= settings.maxRulesCount) {
      return;
    }
    const origin = settings.initRules?.value || [];
    const item = createNewRulesOption();
    rules.value = [...origin, item];
  };

  const remove = (index: number) => {
    if (rules.value.length <= settings.minRulesCount) {
      return;
    }
    const origin = settings.initRules?.value || [];
    rules.value = [...origin];
    rules.value.splice(index, 1);
  };

  const update = (index: number, val: RulesType) => {
    const origin = settings.initRules?.value || [];
    rules.value = [...origin];
    rules.value.splice(index, 1, val);
  };

  return {
    rules,
    add,
    remove,
    update,
  };
}

export function useAudienceTypeRules(opt: { eventSourceAlertVisible: ComputedRef<boolean> }) {
  return {
    audienceTypeValidator: computed(() => [
      {
        validator: (rule, value, callback) => {
          if (opt.eventSourceAlertVisible.value) {
            // 错误信息已经通过 banner 展示，这里的 message 会隐藏
            return callback(
              new ValidationError({
                message: i18n.t('module_dpa_ad_retarget_no_bound_hint'),
                messageI18nKey: 'module_dpa_ad_retarget_no_bound_hint',
                moduleName: i18n.t('adgroup_audience_target'),
                subModuleName: i18n.t('adgroup_audience'),
              }),
            );
          }
          return callback();
        },
        trigger: 'change',
      },
    ]),
  };
}

export function useAudienceEventRules() {
  const includeErrors: Ref<string[]> = ref([]);
  const excludeErrors: Ref<string[]> = ref([]);
  const list2map = (list: RulesType[]) =>
    list.reduce((pre, cur) => {
      const key = cur.code;
      pre[key] = cur.days;
      return pre;
    }, {});

  const checkIsNotNull = (data: RulesType[]): string[] => {
    const errors: string[] = [];
    data &&
      data.forEach((item, index) => {
        const isNotNull = item.code && item.days;
        const msg = item.code ? i18n.t('module_dpa_ad_time_empty') : i18n.t('module_dpa_ad_code_empty');
        const error = isNotNull ? '' : msg;
        errors[index] = error as string;
      });
    return errors;
  };

  const checkIsDuplicated = (data: RulesType[]): string[] => {
    const hasUsedInCodeHash = {};
    const errors: string[] = [];
    for (let i = 0, len = data.length; i < len; i++) {
      const { code } = data[i];
      if (!code) {
        errors[i] = '';
        break;
      }
      if (hasUsedInCodeHash[code]) {
        errors[i] = i18n.t('module_dpa_ad_include_duplicated') as string;
      }
      hasUsedInCodeHash[code] = true;
    }
    return errors;
  };

  const checkIsCrossDays = (includeList: RulesType[], excludeList: RulesType[]) => {
    const excludeMap = list2map(excludeList);
    const errors: string[] = [];
    for (let i = 0, len = includeList.length; i < len; i++) {
      const { code, days } = includeList[i];
      const isDaysInvalid = excludeMap[code] && Number(excludeMap[code]) >= Number(days);
      errors.push(isDaysInvalid ? (i18n.t('module_dpa_ad_include_identical') as string) : '');
    }
    return errors;
  };

  const mergeErrors = (origin: string[], other: string[]) => {
    other.forEach((item, index) => {
      if (!origin[index]) {
        origin[index] = item;
      }
    });
    return [...origin];
  };

  const validate = (formData: AdgroupFormData): boolean => {
    const includeList = computed(() => formData.retargetingEventInclude);
    const excludeList = computed(() => formData.retargetingEventExclude);
    includeErrors.value = [];
    excludeErrors.value = [];
    if (formData.appRetargetingType !== AppRetargetingType.INCLUDE_SPECIFIC_EVENTS) {
      return true;
    }
    if (includeList.value?.length) {
      // 完整性校验
      const errorsForNull = checkIsNotNull(includeList.value);
      // 重复性校验
      const errorsForDuplicated = checkIsDuplicated(includeList.value);
      // 合并校验结果
      includeErrors.value = mergeErrors(errorsForNull, errorsForDuplicated);
    }
    if (excludeList.value?.length) {
      // 完整性校验
      const errorsForNull = checkIsNotNull(excludeList.value);
      // 重复性校验
      const errorsForDuplicated = checkIsDuplicated(excludeList.value);
      // 合并校验结果
      excludeErrors.value = mergeErrors(errorsForNull, errorsForDuplicated);
    }
    // 判断两者是否存在交集，include 中的天数必须大于 exclude 的天数
    if (includeList.value?.length && excludeList.value?.length) {
      const errors = checkIsCrossDays(includeList.value, excludeList.value);
      includeErrors.value = mergeErrors(includeErrors.value, errors);
      excludeErrors.value = mergeErrors(excludeErrors.value, errors);
    }
    return !(includeErrors.value.some(item => item) || excludeErrors.value.some(item => item));
  };

  const audienceEventValidator = computed(() => [
    {
      validator: (_rule, _value, callback, data) => {
        const validStatue = validate(data);
        if (!validStatue) {
          return callback('invalid');
        }
        return callback();
      },
      trigger: 'change',
    },
  ]);

  return {
    includeErrors,
    excludeErrors,
    validate,
    audienceEventValidator,
  };
}

// eslint-disable-next-line max-lines-per-function
export function useAudienceRetargetRule(opt: {
  viewedSelect?: Ref<number>;
  addedSelect?: Ref<number>;
  includeCustomActions?: Ref<{ code: number; days: number }[]>;
  excludeCustomActions?: Ref<{ code: number; days: number }[]>;
}) {
  const { viewedSelect, addedSelect, includeCustomActions, excludeCustomActions } = opt;

  /** 是否有时间没有填写 */
  const isDayEmpty = customActions => customActions.some(item => !item.days);
  /** 是否选择的类型存在重复 */
  const isRepeat = customActions => {
    let [view, add, purchase] = [0, 0, 0];
    customActions.forEach(item => {
      switch (item.code) {
        case 4002:
          view++;
          break;
        case 4003:
          add++;
          break;
        case 4006:
          purchase++;
          break;
        default:
      }
    });
    return view > 1 || add > 1 || purchase > 1;
  };
  /** 是否同种类型 include <= exclude 的时间*/
  const isRangeInvalid = (includeActions, excludeActions) => {
    const isIncludeLessThanExclude = includeActions.some(inItem => {
      const curItemStatus = excludeActions.some(exItem => inItem.code === exItem.code && inItem.days <= exItem.days);
      return curItemStatus;
    });
    return isIncludeLessThanExclude;
  };

  return {
    viewedValidator: computed(() => [
      {
        validator: (rule, value, callback) => {
          if (!viewedSelect.value) {
            return callback(
              new ValidationError({
                messageI18nKey: 'module_dpa_ad_time_empty',
                message: i18n.t('module_dpa_ad_time_empty'),
                moduleName: i18n.t('adgroup_audience_target'),
                subModuleName: i18n.t('adgroup_audience'),
              }),
            );
          }
          return callback();
        },
        trigger: 'change',
      },
    ]),
    addedValidator: computed(() => [
      {
        validator: (rule, value, callback) => {
          if (!addedSelect.value) {
            return callback(
              new ValidationError({
                messageI18nKey: 'module_dpa_ad_time_empty',
                message: i18n.t('module_dpa_ad_time_empty'),
                moduleName: i18n.t('adgroup_audience_target'),
                subModuleName: i18n.t('adgroup_audience'),
              }),
            );
          }
          return callback();
        },
        trigger: 'change',
      },
    ]),
    includeAndExcludeValidator: computed(() => [
      {
        validator: (rule, value, callback) => {
          const { field } = rule;
          let actions = [];
          if (field === 'includeCustomActions') {
            actions = includeCustomActions.value;
          } else if (field === 'excludeCustomActions') {
            actions = excludeCustomActions.value;
          }
          // 校验是否有没填写时间
          if (isDayEmpty(actions)) {
            return callback(
              new ValidationError({
                messageI18nKey: 'module_dpa_ad_time_empty',
                message: i18n.t('module_dpa_ad_time_empty'),
                moduleName: i18n.t('adgroup_audience_target'),
                subModuleName: i18n.t('adgroup_audience'),
              }),
            );
          }
          // 判断是否有重复类型
          if (isRepeat(actions)) {
            return callback(
              new ValidationError({
                messageI18nKey: 'module_dpa_ad_include_duplicated',
                message: i18n.t('module_dpa_ad_include_duplicated'),
                moduleName: i18n.t('adgroup_audience_target'),
                subModuleName: i18n.t('adgroup_audience'),
              }),
            );
          }
          // 判断两者是否存在交集，include中的天数小于exclude的天数
          if (isRangeInvalid(includeCustomActions.value, excludeCustomActions.value)) {
            return callback(
              new ValidationError({
                messageI18nKey: 'module_dpa_ad_include_identical',
                message: i18n.t('module_dpa_ad_include_identical'),
                moduleName: i18n.t('adgroup_audience_target'),
                subModuleName: i18n.t('adgroup_audience'),
              }),
            );
          }
          return callback();
        },
        trigger: 'custom',
      },
    ]),
  };
}

export function useAudienceBaseLabel(opt: { isRetargeting: Ref<boolean>; isProductSales: Ref<boolean> }) {
  const { isRetargeting, isProductSales } = opt;
  const audienceIncludeLabel = computed(() => {
    if (isRetargeting.value) {
      return i18n.t('module_adgroup_targeting_audience_retargeting_include');
    } else {
      return i18n.t('module_adgroup_included_audience');
    }
  });
  const audienceExcludeLabel = computed(() => {
    if (isProductSales.value) {
      return i18n.t('module_dpa_ad_custom_exclude_title');
    } else if (isRetargeting.value) {
      return i18n.t('modele_adgroup_targeting_audience_retargeting_exclude');
    } else {
      return i18n.t('adgroup_audicence_dmp_excluded');
    }
  });
  return {
    audienceIncludeLabel,
    audienceExcludeLabel,
  };
}

export function useExtendAudienceTypeOptions(opt: {
  createMode: Ref<CreateMode>;
  adgroupData: Ref<AdgroupFormData>;
  campaignData: Ref<CampaignFormData>;
}) {
  const { createMode, adgroupData, campaignData } = opt;
  const audienceTypeOptions = ref(AUDIENCE_TYPE);
  const { NORMAL, RETARGET } = audienceTypeOptions.value;
  const { isEdit } = useAdgroupMode({ createMode });
  const { isDedicateCampaign } = useAdgroupIos14({
    adgroupFormData: adgroupData,
    campaignFormData: campaignData,
  });

  const { isOnlyPangle, isWebsite, isAppDownload } = useStdAdgroupIs({
    adgroupFormData: adgroupData,
    campaignFormData: campaignData,
  });

  const { isSplitTestLevelEnabled } = useSplitTestEnabled({ adgroupData, campaignData });

  const { isPangleWebRegrate } = useAllowlist();

  const isDisableRetarget = computed(() => isWebsite.value && isOnlyPangle.value && !isPangleWebRegrate.value);

  // 如果选了app，重和非重会影响cretive的数据，所以需要设灰
  const isSplitTestDisabled = computed(() => isSplitTestLevelEnabled.value && isAppDownload.value);

  const retargetHint = computed(() => {
    if (isEdit.value || isSplitTestDisabled.value) {
      return '';
    }
    if (isDisableRetarget.value) {
      return 'pangle_not_support_web_retargeting';
    }
    return RETARGET.disableHint;
  });

  const extendAudienceTypeOptions = computed(() => ({
    NORMAL: {
      ...NORMAL,
      disabled:
        (isEdit.value && adgroupData.value.dpaLocalAudience === AUDIENCE_TYPE.RETARGET.value) ||
        isSplitTestDisabled.value,
      disableHint: isEdit.value || isSplitTestDisabled.value ? '' : NORMAL.disableHint,
    },
    RETARGET: {
      ...RETARGET,
      disabled:
        (isEdit.value && adgroupData.value.dpaLocalAudience === AUDIENCE_TYPE.NORMAL.value) ||
        isSplitTestDisabled.value ||
        isDedicateCampaign.value ||
        isDisableRetarget.value,
      disableHint: retargetHint.value,
    },
  }));
  return {
    extendAudienceTypeOptions,
  };
}

export function usePropsEventSourceAlertVisible(opt: {
  catalogInfo: Ref<CatalogListItem>;
  isRetargeting: Ref<boolean>;
}) {
  const { catalogInfo, isRetargeting } = opt;

  // 重定向没有绑定事件源则需要提示
  const eventSourceAlertVisible = computed(() => {
    if (!isRetargeting.value) {
      return false;
    }
    if (!isNumber(catalogInfo.value?.event_source_count)) {
      return false;
    }
    return !catalogInfo.value.event_source_count;
  });
  return {
    eventSourceAlertVisible,
  };
}

export function usePropsEventsSourceCheckAlertVisible(opt: { catalogInfo: Ref<CatalogListItem> }) {
  const { catalogInfo } = opt;

  // 已经绑定事件源去检查是否够量提示
  const eventsSourceCheckAlertVisible = computed(() => catalogInfo.value.event_source_count > 0);

  return {
    eventsSourceCheckAlertVisible,
  };
}

export function usePropsAntiDiscriminationVisible(opt: { adgroupData: Ref<AdgroupFormData> }) {
  const { state } = useStore();
  const dmpList = computed(() => state[StateEnum.dmpList].data.value ?? []);
  const dmpMap = computed(() =>
    dmpList.value?.reduce((result, item) => {
      result[item.id] = item;
      return result;
    }, {}),
  );
  const targetAudience = [AppRetargetingAudiencePackageType.CUSTOMER_FILE, AppRetargetingAudiencePackageType.LIVERAMP];
  const antiDiscriminationVisible = computed(() => {
    const { retargetingTags, retargetingTagsExclude } = opt.adgroupData.value;
    return (
      retargetingTags.some(item => targetAudience.includes(dmpMap.value[item]?.audienceType)) ||
      retargetingTagsExclude.some(item => targetAudience.includes(dmpMap.value[item]?.audienceType))
    );
  });

  // 判断目标类型的 audience 是否已经被删除
  // antiDiscrimination 为 1，但是 retargetingTags 和 retargetingTagsExclude 中没有目标类型的受众数据
  const targetAudienceDataHasDeleted = computed(() => {
    const { antiDiscrimination } = opt.adgroupData.value;
    const hasNoTargetAudience = !antiDiscriminationVisible.value;
    const isEmptyDmpList = dmpList.value?.length === 0;
    return Boolean(antiDiscrimination) && hasNoTargetAudience && !isEmptyDmpList;
  });
  return {
    antiDiscriminationVisible,
    targetAudienceDataHasDeleted,
  };
}

export function useAntiDiscriminationRule() {
  return {
    antiDiscriminationValidator: computed(() => [
      {
        validator: (rule, value, callback) => {
          if (!value) {
            return callback(
              new ValidationError({
                message: i18n.t('module_adgroup_targeting_anti_error_msg'),
                messageI18nKey: 'module_adgroup_targeting_anti_error_msg',
                moduleName: i18n.t('adgroup_audience_targetrget'),
                subModuleName: i18n.t('adgroup_audience'),
              }),
            );
          }
          return callback();
        },
        trigger: 'change',
      },
    ]),
  };
}
export function useAudiencePackageTagOffline() {
  const { getStore } = useStore();
  // 不在hook里请求，只处理状态
  const { data: audiencePackageTagsInfo } = getStore(NetStateEnum.audiencePackageTagsInfo, undefined, false, false);
  const includeSelectedOfflineTagArr = computed(() =>
    (audiencePackageTagsInfo.value?.retargetingTags || []).filter(
      item => item.statusInfo.status === AudiencePackageItemStatus.invalid,
    ),
  );
  const excludeSelectedOfflineTagArr = computed(() =>
    (audiencePackageTagsInfo.value?.retargetingTagsExclude || []).filter(
      item => item.statusInfo.status === AudiencePackageItemStatus.invalid,
    ),
  );

  const offlineIncludeTagTips = computed(() => {
    if (!includeSelectedOfflineTagArr.value.length) {
      return '';
    }

    return i18n.t('module_adgroup_targeting_audience_package_offline_tip', {
      num: includeSelectedOfflineTagArr.value.length ?? 0,
      package_Names: includeSelectedOfflineTagArr.value.map(item => item.name).join(', '),
    });
  });

  const offlineExcludeTagTips = computed(() => {
    if (!excludeSelectedOfflineTagArr.value.length) {
      return '';
    }

    return i18n.t('module_adgroup_targeting_audience_package_offline_tip', {
      num: excludeSelectedOfflineTagArr.value.length ?? 0,
      package_Names: excludeSelectedOfflineTagArr.value.map(item => item.name).join(', '),
    });
  });
  return {
    includeSelectedOfflineTagArr,
    excludeSelectedOfflineTagArr,
    offlineExcludeTagTips,
    offlineIncludeTagTips,
  };
}

/**
 * 是否选择了 Premium age 人群包
 * @param adgroupData adgroup 数据
 * @returns
 */
export function useAudienceSelected(opt: { adgroupData: Ref<AdgroupFormData> }) {
  const isSelectPremiumAge = computed(() => {
    const { adgroupData } = opt;
    const { getStore } = useStore();
    const { data: dmpInfo } = getStore(NetStateEnum.dmpList);
    const dmp = (dmpInfo.value ?? []) as AudiencePackageItem[];
    const premiumAge =
      selectedPremiumAgeInfo(adgroupData.value.retargetingTags, dmp) ||
      selectedPremiumAgeInfo(adgroupData.value.retargetingTagsExclude, dmp);
    return Boolean(premiumAge);
  });

  return {
    isSelectPremiumAge,
  };
}
